/* eslint-disable max-lines-per-function */
import { Link, Routes, Route, useParams } from "react-router-dom";
import React from "react";
import { Grid } from "@mui/material";
import { useQuery } from "@apollo/client";
import { Viewer } from "components/auth/types";
import { authorizeForRole } from "components/auth/authorize-for-role";
import { LoadingSpinner, ServerError } from "components/graphql-views";
import NotFound from "pages/not-found";
import { ProcessRemovalsView,
  ProcessRemovalsViewVariables,
  UserRole } from "types/gql-types";
import { ContainerCardLayout } from "components/haven-design/card/index";
import { Channel } from "../../types";
import { PROCESS_REMOVALS_VIEW } from "../query";
import { Template } from "../types";
import styles from "./index.module.css";
import StepTemplate from "./step-choose-template";
import StepRemovals from "./step-removals";
import StepReview from "./step-review-send";
import StepSuccess from "./step-success";

type OwnProps = {
  "*"?: string;
}

const activeLinkStyle = {
  textDecoration: "underline",
  fontWeight: 800,
};

const ProcessUpdateDenials: React.FC<OwnProps & { viewer: Viewer }> = (props) => {
  const { listId } = useParams();
  const [emailTemplate, setEmailTemplate] = React.useState<Template | null>(null);
  const [smsTemplate, setSmsTemplate] = React.useState<Template | null>(null);
  const [letterTemplate, setLetterTemplate] = React.useState<Template | null>(null);
  const [hasSkippedLetters, setSkipLetters] = React.useState(false);
  const currentPageName = props["*"];

  const results =
  useQuery<ProcessRemovalsView, ProcessRemovalsViewVariables>(PROCESS_REMOVALS_VIEW, {
    variables: { list: listId ?? "",
      limit: 50,
      offset: 0 },
    fetchPolicy: "cache-first",
  });

  const { loading, error, data } = results;

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return <ServerError desc="Missing Data" />;

  const letterStepComplete = hasSkippedLetters ? true : !!letterTemplate;
  const allStepsComplete = !!(emailTemplate && smsTemplate && letterStepComplete);
  const removedRequests = data.interestList.scheduledUpdate.removals.results;
  const title = () => (<><Link to={`/dash/manager/update/${listId}`}>
    {data.interestList.name} Waitlist Update
  </Link>{" "}&ndash; Process Removals</>);
  return (
    <ContainerCardLayout title={title()}>
      <Grid container spacing={2}>
        <Grid size={{ md: 2 }}>
          <Link
            to={`/dash/manager/update/${listId}/process/removals`}
            className={styles.sideBarItem}
            style={currentPageName === "removals" ? activeLinkStyle : {}}
          >
        Review Applicants
          </Link>

          <Link
            to={`/dash/manager/update/${listId}/process/review`}
            className={`${styles.sideBarItem}`}
            style={currentPageName === "review" ? activeLinkStyle : {}}
          >
        Review & Send
          </Link>
        </Grid>

        <Grid size={{ md: 10 }}>
          <Routes>
            <Route path="removals/*" element={<StepRemovals results={results} />} />

            <Route path="choose-email/*" element={
              <StepTemplate
                requests={removedRequests}
                templates={data.templates}
                setTemplate={setEmailTemplate}
                currentTemplate={emailTemplate}
                channel={Channel.EMAIL}
                isStepComplete={!!emailTemplate}
              />
            } />

            <Route path="choose-sms/*" element={
              <StepTemplate
                requests={removedRequests}
                templates={data.templates}
                setTemplate={setSmsTemplate}
                currentTemplate={smsTemplate}
                channel={Channel.SMS}
                isStepComplete={!!smsTemplate}
              />
            } />

            <Route path="choose-letter/*" element={
              <StepTemplate
                requests={removedRequests}
                templates={data.templates ?? []}
                setTemplate={setLetterTemplate}
                currentTemplate={letterTemplate}
                channel={Channel.LETTER}
                isStepComplete={letterStepComplete}
                hasSkippedStep={hasSkippedLetters}
                setSkipStep={setSkipLetters}
              />
            } />

            <Route path="review/*" element={
              <StepReview
                results={results}
                letterTemplate={letterTemplate}
                emailTemplate={emailTemplate}
                smsTemplate={smsTemplate}
                stepComplete={allStepsComplete}
                hasSkippedLetters={hasSkippedLetters}
                viewer={props.viewer}
              />
            } />

            <Route path="success/*" element={<StepSuccess />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Grid>
      </Grid>
    </ContainerCardLayout>
  );
};

export default authorizeForRole<Record<string, unknown>>([
  UserRole.PROPERTY_MANAGER,
  UserRole.SUPERVISOR,
], ProcessUpdateDenials as unknown as React.FC<Record<string, unknown>>);
